import { useContext } from 'react'
import { StudentWithScores, AssessmentContext, SavingCallbacks } from 'contexts/assessment'
import { BookingContext } from 'contexts/booking'
import { saveClassResult, adaptStudentResult } from 'api/unit-assessment'
import { ExceptionType, GOCException } from 'exceptions'

export const useSaveEncounterResults = (students: StudentWithScores[], saving: () => SavingCallbacks):
(isClassResultChanged: boolean, currentStudent: StudentWithScores) => Promise<void> => {
    const assessment = useContext(AssessmentContext)
    const booking = useContext(BookingContext)

    const save = async (isClassResultChanged: boolean = false, currentStudent: StudentWithScores): Promise<void> => {
        if (booking.status === 'validated' && assessment.status === 'encounter') {
            const [done, error] = saving()
            if (isClassResultChanged) {
                try {
                    await saveClassResult(
                        booking.auth,
                        assessment.classDetails.classId,
                        adaptStudentResult(currentStudent, true)
                    )
                } catch (e) {
                    error(e as Error)
                    throw e
                }
            } else {
                for (const student of students) {
                    if (!student.isNoShowTechResultsSelected && student.finalScore === void 0) {
                        throw new GOCException(ExceptionType.AssessmentNotCompleted)
                    }
                }

                const results = await Promise.allSettled(
                    students.map(async student => saveClassResult(
                        booking.auth,
                        assessment.classDetails.classId,
                        adaptStudentResult(student, true)
                    ))
                )

                const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected')
                if (failures.length > 0) {
                    const reasons = failures.map(f => String(f.reason)).join('; ')
                    const err = new Error(`Failed to save ${failures.length} of ${students.length} students: ${reasons}`)
                    error(err)
                    throw err
                }
            }
            done()
        }
    }
    return save
}
